import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');
  const paymentId = searchParams.get('payment_id');
  const chargeId = searchParams.get('charge_id');
  const [reconciling, setReconciling] = useState(true);
  const [reconcileStatus, setReconcileStatus] = useState('');
  const [reconcileError, setReconcileError] = useState('');

  // Reconcile with PayMongo when this page loads so we don't depend solely on webhooks
  useEffect(() => {
    const idToCheck = paymentId || bookingId;
    if (!idToCheck && !chargeId) {
      setReconciling(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const url = chargeId
          ? `${API}/charges/${chargeId}/reconcile`
          : `${API}/payments/${idToCheck}/reconcile`;
        const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        const st = res.data?.status;
        if (st === 'captured' || st === 'paid' || st === 'settled') {
          setReconcileStatus('Payment confirmed and recorded.');
        } else {
          setReconcileStatus(res.data?.message || 'Payment is being processed.');
        }
      } catch (err) {
        if (!cancelled) {
          setReconcileError(
            err.response?.data?.detail ||
              'Could not verify payment status. If you have already paid, it will be reflected shortly.'
          );
        }
      } finally {
        if (!cancelled) setReconciling(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId, paymentId, chargeId]);

  useEffect(() => {
    // Auto-redirect after 6 seconds (a bit longer to let reconcile finish)
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 6000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-success-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
          <p className="text-gray-600 mb-4">
            Your payment has been processed. The provider will be notified to begin work on your job.
          </p>

          {/* Live reconcile status */}
          <div
            className={
              'mb-6 rounded-lg p-3 text-sm border ' +
              (reconcileError
                ? 'bg-red-50 border-red-200 text-red-700'
                : reconcileStatus.includes('confirmed')
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700')
            }
            data-testid="payment-reconcile-status"
          >
            {reconciling ? (
              <span className="inline-flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Verifying with PayMongo…
              </span>
            ) : reconcileError ? (
              reconcileError
            ) : (
              reconcileStatus || 'Payment recorded.'
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>🔒 Escrow Protection:</strong> Your payment is securely held until the job is completed and you confirm satisfaction.
            </p>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold"
            data-testid="go-to-dashboard-btn"
          >
            Go to Dashboard
          </button>

          <p className="text-sm text-gray-500 mt-4">
            Redirecting automatically in 6 seconds...
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
